import React, { useEffect } from "react";
import SVG from "@/components/atoms/SVGAsset/SVGAsset";
import Spinner from "@/components/atoms/Spinner/Spinner";

import styles from "./styles.module.css";
import { useAuth } from "@/contexts/AuthContext";
import { useGetCompaniesByUserId, useGetExternalToken } from "@/services/Login";
import { CurrentCompany } from "@/types";
import { useNavigate } from "react-router-dom";
import { setUserContextIds } from "@/utils/sessionStorage";
import { useTranslation } from "react-i18next";
import { notifications } from "@mantine/notifications";
import { ErrorNotificationObj } from "@/utils/NotificationUtil";

export default function ExternalAuthSession({ code }: { code: string }) {
  const { getPort, setUser, setCompanies, setCurrentCompany } = useAuth();
  const navigate = useNavigate();

  const port = getPort();

  const { data: user, isLoading: isLoadingUser, error: userError } = useGetExternalToken(code, port);
  const { t } = useTranslation();

  const { data: companies, isLoading: isLoadingCompanies } = useGetCompaniesByUserId(
    user?.id as number,
    user?.role?.[0]?.companyId as number,
    { enabled: !!(user && user.role.length > 0), retry: false }
  );

  useEffect(() => {
    if (!userError) return;
    if (userError) {
      const errMessage = userError?.message;
      notifications.show(
        ErrorNotificationObj(t("notifications:auth.authSession"), errMessage || t("notifications:generic.unknownError"))
      );
    }
  }, [userError]);

  useEffect(() => {
    if (isLoadingUser || isLoadingCompanies) return;
    if (!user) {
      return;
    }
    if (user) {
      setUser(user);
    }
    const comps = companies?.companies ?? [];
    if (comps.length > 0) {
      setCompanies(comps);
      setCurrentCompany(comps[0] as CurrentCompany);
      setUserContextIds({
        userId: user.id,
        companyId: comps[0].companyId,
      });
      navigate("/dashboard", { replace: true });
    } else {
      setUserContextIds({
        userId: user.id,
        companyId: null,
      });
      navigate("/registerCompany", { replace: true });
    }
  }, [isLoadingUser, isLoadingCompanies, user, companies, setUser, setCompanies, setCurrentCompany, history]);

  return (
    <div className={styles.loginRoot}>
      <Spinner loading={isLoadingUser || isLoadingCompanies} />
      <SVG className="logo" name="reviver-logo"></SVG>
      {!!code && <h3>{t("common:loggingIn")}</h3>}
      {!code && <h3>{t("common:redirecting")}</h3>}
    </div>
  );
}
